import {
  ApolloClient,
  from,
  HttpLink,
  InMemoryCache,
  split,
} from '@apollo/client';
import { getMainDefinition } from '@apollo/client/utilities';
import { version } from '../../package.json';
import errorLink from './links/error';
import loggingLink from './links/logging';
import WebSocketLink from './links/websocket';
import possibleTypes from './possibleTypes.json';

const GRAPHQL_URI = '/api/graphql';

const httpLink = new HttpLink({
  uri: GRAPHQL_URI,
  headers: {
    'X-Requested-With': 'fetch',
  },
});

const getCorrectProtocol = () =>
  window.location.protocol === 'http:' ? 'ws' : 'wss';

const getWsUri = (path: string) => {
  // Because of https://github.com/facebook/create-react-app/issues/8094 in development we have to connect to API directly
  // websockets don't adhere to same-site policy so no cors necessary
  if (process.env.NODE_ENV === 'development') {
    const apiUrl = process.env.REACT_APP_API_URL?.replace(
      'https',
      'wss',
    )?.replace('http', 'ws');
    return `${apiUrl}${path}`;
  }
  return `${getCorrectProtocol()}://${window.location.host}${path}`;
};

const wsLink = new WebSocketLink({
  url: getWsUri(GRAPHQL_URI),
  connectionParams: {
    langs: navigator.languages,
  },
  lazy: true,
  retryAttempts: 5,
  retryWait: async () => {
    await new Promise((resolve) =>
      setTimeout(resolve, 1000 + Math.random() * 3000),
    );
  },
  isFatalConnectionProblem: () => {
    return false;
  },
});

const requestLink = split(
  // split based on operation type
  ({ query }) => {
    const definition = getMainDefinition(query);
    return (
      definition.kind === 'OperationDefinition' &&
      definition.operation === 'subscription'
    );
  },
  wsLink,
  httpLink,
);

export default function createClient() {
  return new ApolloClient({
    name: 'Game',
    version: version,
    cache: new InMemoryCache({
      possibleTypes,
      typePolicies: {
        Language: {
          keyFields: ['code'],
        },
      },
    }),
    link: from([errorLink, loggingLink, requestLink]),
    assumeImmutableResults: true,
  });
}
